import React, { useState, useMemo, useRef, useCallback } from 'react';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import {
  STUDENT_TEMPLATE_COLUMNS,
  STUDENT_TEMPLATE_MANDATORY_COLUMNS
} from '../../constants/studentTemplate';

const ACCEPTED_EXTENSIONS = ['xlsx', 'xls', 'csv'];

const STATUS_STYLES = {
  pending: 'bg-slate-100 text-slate-800',
  uploading: 'bg-amber-100 text-amber-800',
  success: 'bg-emerald-100 text-emerald-900 font-semibold border border-emerald-200',
  'already-registered': 'bg-green-100 text-green-900 font-semibold border border-green-200',
  error: 'bg-rose-100 text-rose-700',
  blocked: 'bg-rose-50 text-rose-600',
  skipped: 'bg-gray-100 text-gray-500'
};

const SUCCESS_STATUSES = new Set(['success', 'already-registered']);
const PROCESSED_STATUSES = new Set(['success', 'already-registered', 'error']);

const sanitizeText = (value) => (value === undefined || value === null ? '' : String(value).trim());

const normalizeGender = (value) => {
  const normalized = sanitizeText(value).toLowerCase();
  if (!normalized) return '';
  if (['male', 'm'].includes(normalized)) return 'Male';
  if (['female', 'f'].includes(normalized)) return 'Female';
  if (['other', 'o'].includes(normalized)) return 'Other';
  return '';
};

const cleanPayload = (payload) => {
  const next = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      next[key] = value;
    }
  });
  return next;
};

const formatIsoDate = (value) => {
  if (value === undefined || value === null || value === '') return '';

  if (typeof value === 'number' && XLSX.SSF && typeof XLSX.SSF.parse_date_code === 'function') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      const jsDate = new Date(Date.UTC(parsed.y, parsed.m - 1, parsed.d));
      return Number.isNaN(jsDate.getTime()) ? '' : jsDate.toISOString().slice(0, 10);
    }
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString().slice(0, 10);
  }

  const raw = sanitizeText(value);
  if (!raw) return '';

  const normalized = raw.replace(/[.]/g, '/');

  if (/^\d{2}[\/\-]\d{2}[\/\-]\d{4}$/.test(normalized)) {
    const safe = normalized.replace(/-/g, '/');
    const [day, month, year] = safe.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }

  const parsedDate = new Date(raw);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toISOString().slice(0, 10);
  }

  return '';
};

const buildRow = (record, rowNumber) => {
  const studentId = sanitizeText(record['Student ID']);
  const fullName = sanitizeText(record['Full Name']);
  const dobInput = record['Date of Birth (dd/mm/yyyy)'];
  const dob = formatIsoDate(dobInput);
  const genderInput = record['Gender (Male, Female)'];
  const gender = normalizeGender(genderInput);
  const nicNumber = sanitizeText(record['NIC Number']);
  const telephone = sanitizeText(record['Telephone']);
  const email = sanitizeText(record['Email']);
  const address = sanitizeText(record['Address']);
  const registrationNumber = sanitizeText(record['Registration Number']);
  const universityID = sanitizeText(record['University ID']);
  const universityRegisterDateInput = record['University Register Date (dd/mm/yyyy)'];
  const universityRegisterDate = formatIsoDate(universityRegisterDateInput);
  const batchRaw = sanitizeText(record['Batch ID (From kaaryaalaya)']);
  const batchId = batchRaw ? Number(batchRaw) : null;
  const verifiedName = sanitizeText(record['Name Verification']) || fullName;

  const errors = [];
  const invalidColumns = {};
  if (!studentId) errors.push('Student ID is required');
  if (!fullName) errors.push('Full name is required');
  if (!registrationNumber) errors.push('Registration number is required');
  if (!batchRaw) {
    errors.push('Batch ID is required');
  } else if (!Number.isFinite(batchId)) {
    errors.push('Batch ID must be numeric');
  }

  if (dobInput && !dob) {
    invalidColumns['Date of Birth (dd/mm/yyyy)'] = 'Use dd/mm/yyyy format (e.g., 31/01/2004)';
  }

  if (genderInput && !gender) {
    invalidColumns['Gender (Male, Female)'] = 'Use Male or Female';
  }

  if (universityRegisterDateInput && !universityRegisterDate) {
    invalidColumns['University Register Date (dd/mm/yyyy)'] = 'Use dd/mm/yyyy format (e.g., 31/01/2024)';
  }

  const studentPayload = cleanPayload({
    studentId,
    fullName,
    email,
    telephone,
    dob,
    gender,
    nicNumber,
    address
  });

  const registrationPayload = cleanPayload({
    studentId,
    batchId: Number.isFinite(batchId) ? batchId : undefined,
    registrationNumber,
    verifiedName,
    universityID,
    universityRegisterDate,
    registrationStatus: 'active'
  });

  return {
    rowNumber,
    rawRecord: record,
    preview: {
      studentId,
      fullName,
      registrationNumber,
      batchId: Number.isFinite(batchId) ? batchId : batchRaw || ''
    },
    studentPayload,
    registrationPayload,
    errors,
    invalidColumns,
    status: errors.length ? 'blocked' : 'pending',
    message: errors.length ? 'Resolve the validation issues before uploading.' : ''
  };
};

export default function UploadStudents() {
  const fileInputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [fileMeta, setFileMeta] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const user = AuthService.getUser();
  const hasAdminAccess = Boolean(
    user?.superAdmin?.superAdminId || user?.superAdmin?.staff?.type === 'admin'
  );

  const readyRows = useMemo(() => rows.filter((row) => row.errors.length === 0), [rows]);
  const blockedRows = useMemo(() => rows.filter((row) => row.errors.length > 0), [rows]);
  const successCount = useMemo(() => rows.filter((row) => SUCCESS_STATUSES.has(row.status)).length, [rows]);
  const failedCount = useMemo(() => rows.filter((row) => row.status === 'error').length, [rows]);
  const processedCount = useMemo(
    () => rows.filter((row) => row.errors.length === 0 && PROCESSED_STATUSES.has(row.status)).length,
    [rows]
  );
  const progressPercent = readyRows.length ? Math.round((processedCount / readyRows.length) * 100) : 0;

  const patchRow = useCallback((rowNumber, updates) => {
    setRows((prev) => prev.map((row) => (row.rowNumber === rowNumber ? { ...row, ...updates } : row)));
  }, []);

  const resetWorkspace = useCallback(() => {
    setRows([]);
    setFileMeta(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleDownloadTemplate = useCallback(() => {
    try {
      const sheet = XLSX.utils.aoa_to_sheet([STUDENT_TEMPLATE_COLUMNS]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, 'Students');
      const timestamp = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `Student_Import_Template_${timestamp}.xlsx`);
    } catch (error) {
      console.error('Template download failed:', error);
      Swal.fire('Download failed', 'Unable to create the template. Please try again.', 'error');
    }
  }, []);

  const checkStudentRegistrationStatus = useCallback(async (studentId, registrationNumber) => {
    const trimmedStudentId = sanitizeText(studentId);
    const trimmedRegistration = sanitizeText(registrationNumber);

    if (!trimmedStudentId) {
      return { studentExists: false, registrationExists: false };
    }

    try {
      const { data } = await axiosInstance.get(
        `/admin/students/${encodeURIComponent(trimmedStudentId)}/registration-status`,
        {
          params: trimmedRegistration ? { registrationNumber: trimmedRegistration } : {}
        }
      );

      return {
        studentExists: Boolean(data?.studentExists),
        registrationExists: Boolean(data?.registrationExists),
        registration: data?.registration || null
      };
    } catch (error) {
      console.error('Registration status lookup failed:', error);
      throw new Error(error.response?.data?.message || 'Failed to check existing records');
    }
  }, []);

  const processFile = useCallback(async (file) => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !ACCEPTED_EXTENSIONS.includes(extension)) {
      Swal.fire('Unsupported file', 'Please upload a .xlsx, .xls, or .csv file.', 'warning');
      return;
    }

    setParsing(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      if (!workbook.SheetNames?.length) {
        throw new Error('This workbook does not contain any sheets.');
      }

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const matrix = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        raw: false,
        blankrows: false
      });

      if (!matrix.length) {
        throw new Error('The worksheet is empty.');
      }

      const headerRow = (matrix[0] || []).map((cell) => sanitizeText(cell));
      const missingColumns = STUDENT_TEMPLATE_COLUMNS.filter((column) => !headerRow.includes(column));
      if (missingColumns.length) {
        throw new Error(`Missing required column(s): ${missingColumns.join(', ')}`);
      }

      const dataRows = matrix.slice(1);
      const parsedRows = [];

      dataRows.forEach((row, index) => {
        const record = {};
        headerRow.forEach((header, columnIndex) => {
          if (!header) return;
          record[header] = row[columnIndex] ?? '';
        });

        const isBlank = STUDENT_TEMPLATE_COLUMNS.every((column) => sanitizeText(record[column]) === '');
        if (isBlank) {
          return;
        }

        parsedRows.push(buildRow(record, index + 2));
      });

      if (!parsedRows.length) {
        throw new Error('No data rows were found beneath the header.');
      }

      setRows(parsedRows);
      setFileMeta({
        name: file.name,
        sheet: sheetName,
        total: parsedRows.length,
        importedAt: new Date()
      });

      Swal.fire('File ready', `${parsedRows.length} row(s) parsed successfully.`, 'success');
    } catch (error) {
      console.error('Student template parsing failed:', error);
      setRows([]);
      setFileMeta(null);
      Swal.fire('Unable to read file', error.message || 'Please double-check the template.', 'error');
    } finally {
      setParsing(false);
    }
  }, []);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      await processFile(file);
      event.target.value = '';
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  const handleUpload = async () => {
    if (!readyRows.length) {
      Swal.fire('No valid rows', 'Please fix the highlighted issues before uploading.', 'info');
      return;
    }

    setUploading(true);
    let success = 0;
    let failure = 0;

    const snapshot = [...rows];
    for (const row of snapshot) {
      if (row.errors.length > 0) {
        patchRow(row.rowNumber, { status: 'blocked', message: 'Fix validation issues in the sheet.' });
        continue;
      }

      const studentId = sanitizeText(row.studentPayload.studentId);
      const registrationNumber = sanitizeText(row.registrationPayload.registrationNumber);

      if (!studentId || !registrationNumber) {
        patchRow(row.rowNumber, { status: 'error', message: 'Student ID and Registration Number are required.' });
        failure += 1;
        continue;
      }

      patchRow(row.rowNumber, { status: 'uploading', message: 'Checking existing records...' });

      let lookup;
      try {
        lookup = await checkStudentRegistrationStatus(studentId, registrationNumber);
      } catch (error) {
        patchRow(row.rowNumber, { status: 'error', message: error.message });
        failure += 1;
        continue;
      }

      if (lookup.studentExists && lookup.registrationExists) {
        patchRow(row.rowNumber, {
          status: 'already-registered',
          message: 'Already registered. Skipped.'
        });
        success += 1;
        continue;
      }

      if (!lookup.studentExists) {
        patchRow(row.rowNumber, { status: 'uploading', message: 'Creating student profile...' });
        try {
          await axiosInstance.post('/admin/students', row.studentPayload);
        } catch (error) {
          const serverMessage = error.response?.data?.message || error.message || 'Failed to create student';
          if (!(error.response?.status === 400 && /student id already exists/i.test(serverMessage))) {
            patchRow(row.rowNumber, { status: 'error', message: serverMessage });
            failure += 1;
            continue;
          }
        }
      }

      patchRow(row.rowNumber, { status: 'uploading', message: 'Registering student...' });
      try {
        await axiosInstance.post('/admin/registrations', row.registrationPayload);
        patchRow(row.rowNumber, { status: 'success', message: 'Uploaded successfully.' });
        success += 1;
      } catch (error) {
        const serverMessage = error.response?.data?.message || error.message || 'Failed to register student';
        let handled = false;

        if (/registration number already exists/i.test(serverMessage) || /already has an active registration/i.test(serverMessage)) {
          try {
            const latestLookup = await checkStudentRegistrationStatus(studentId, registrationNumber);
            if (latestLookup.registrationExists) {
              patchRow(row.rowNumber, {
                status: 'already-registered',
                message: 'Already registered. Skipped.'
              });
              success += 1;
              handled = true;
            }
          } catch (lookupError) {
            console.error('Secondary registration lookup failed:', lookupError);
          }
        }

        if (!handled) {
          patchRow(row.rowNumber, { status: 'error', message: serverMessage });
          failure += 1;
        }
      }
    }

    setUploading(false);
    Swal.fire({
      icon: failure ? 'warning' : 'success',
      title: 'Upload finished',
      text: `${success} student${success === 1 ? '' : 's'} processed, ${failure} failed.`,
      confirmButtonColor: '#0f172a'
    });
  };

  const uploadDisabled = uploading || !readyRows.length;

  if (!hasAdminAccess) {
    return (
      <DashboardLayout title="Upload Student List">
        <div className="rounded-3xl border border-rose-100 bg-rose-50 p-10 text-center">
          <h2 className="text-xl font-semibold text-rose-700">Access restricted</h2>
          <p className="mt-2 text-sm text-rose-600">
            Only admin users can upload student lists. Please contact an administrator if you believe this is a mistake.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Upload Student List">
      <div className="space-y-6">
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-800 p-8 text-white shadow-xl">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-3xl space-y-4">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-white/60">Bulk import</p>
                <h1 className="mt-2 text-3xl font-semibold">Upload Student List</h1>
                <p className="mt-3 text-sm text-white/80">
                  Prepare the Excel template with accurate batch IDs from Kaaryaalaya. Each row creates a student
                  profile and registers it to the selected batch in a single pass.
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-white/60">Must-have columns</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {STUDENT_TEMPLATE_MANDATORY_COLUMNS.map((column) => (
                    <span key={column} className="rounded-full bg-white/10 px-3 py-1 text-xs text-white">
                      {column}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleDownloadTemplate}
                className="inline-flex items-center justify-center rounded-xl border border-white/30 bg-white/10 px-6 py-3 text-sm font-semibold text-white shadow-lg backdrop-blur transition hover:bg-white/20"
              >
                <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                </svg>
                Download template
              </button>
              <button
                onClick={resetWorkspace}
                disabled={!rows.length && !fileMeta}
                className="inline-flex items-center justify-center rounded-xl border border-white/20 px-6 py-3 text-sm font-semibold text-white/80 transition hover:text-white disabled:cursor-not-allowed disabled:border-white/10 disabled:text-white/40"
              >
                Reset workspace
              </button>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
            <p className="text-xs font-semibold text-emerald-600">Rows ready</p>
            <p className="mt-2 text-3xl font-bold text-emerald-800">{readyRows.length}</p>
          </div>
          <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4">
            <p className="text-xs font-semibold text-rose-600">Rows with issues</p>
            <p className="mt-2 text-3xl font-bold text-rose-800">{blockedRows.length}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold text-slate-500">Uploaded</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{successCount}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold text-slate-500">Failed</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{failedCount}</p>
          </div>
        </div>

        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
          className="flex cursor-pointer flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-300 bg-white p-10 text-center transition hover:border-indigo-400 hover:bg-indigo-50"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleFileChange}
          />
          <svg className="h-16 w-16 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className="mt-4 text-lg font-semibold text-slate-900">
            {parsing ? 'Analyzing file...' : 'Drop the Excel file or click to browse'}
          </p>
          <p className="mt-2 max-w-xl text-sm text-slate-500">
            Accepts .xlsx, .xls, or .csv files generated from the official template. Formatting, formulas, and colors are
            ignored during import.
          </p>
          {fileMeta && (
            <div className="mt-4 rounded-full bg-slate-100 px-4 py-1 text-xs text-slate-600">
              Loaded {fileMeta.name} • Sheet: {fileMeta.sheet} • {fileMeta.total} rows
            </div>
          )}
        </div>

        {readyRows.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-700">Upload progress</p>
                <p className="text-xs text-slate-500">{progressPercent}% complete</p>
              </div>
              <button
                onClick={handleUpload}
                disabled={uploadDisabled}
                className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
              >
                {uploading ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Uploading...
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16v14a2 2 0 01-2 2H6a2 2 0 01-2-2z" />
                    </svg>
                    Start upload
                  </>
                )}
              </button>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-emerald-500 transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Records preview</h2>
              <p className="text-sm text-slate-500">Validate the data before sending it to Kaaryaalaya.</p>
            </div>
            {rows.length > 0 && (
              <div className="text-sm text-slate-500">
                Showing {rows.length} row{rows.length === 1 ? '' : 's'} • Ready: {readyRows.length} • Issues: {blockedRows.length}
              </div>
            )}
          </div>

          <div className="max-h-[520px] overflow-auto">
            <table className="min-w-max divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Row</th>
                  {STUDENT_TEMPLATE_COLUMNS.map((column) => (
                    <th key={column} className="px-4 py-3">
                      {column}
                    </th>
                  ))}
                  <th className="px-4 py-3">Validation</th>
                  <th className="px-4 py-3">Upload status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {rows.length === 0 ? (
                  <tr>
                    <td
                      className="px-4 py-12 text-center text-sm text-slate-500"
                      colSpan={STUDENT_TEMPLATE_COLUMNS.length + 3}
                    >
                      Upload the Excel template to preview student rows.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const statusStyle = STATUS_STYLES[row.status] || STATUS_STYLES.pending;
                    const rowHighlightClass = SUCCESS_STATUSES.has(row.status) ? 'bg-emerald-50/80' : '';
                    const messageClass = SUCCESS_STATUSES.has(row.status)
                      ? 'text-emerald-700'
                      : row.status === 'error'
                        ? 'text-rose-600'
                        : 'text-slate-500';
                    return (
                      <tr key={row.rowNumber} className={`align-top transition ${rowHighlightClass}`}>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">{row.rowNumber}</td>
                        {STUDENT_TEMPLATE_COLUMNS.map((column) => {
                          const cellValue = sanitizeText(row.rawRecord?.[column] ?? '');
                          const warning = row.invalidColumns?.[column];
                          const cellClasses = warning
                            ? 'px-4 py-3 align-top bg-amber-50 text-amber-800 border border-amber-100'
                            : 'px-4 py-3 text-slate-700';
                          return (
                            <td key={`${row.rowNumber}-${column}`} className={cellClasses}>
                              <span className={warning ? 'font-semibold text-amber-800' : ''}>{cellValue || '—'}</span>
                              {warning && (
                                <p className="mt-1 text-xs text-amber-700">{warning}</p>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-4 py-3">
                          {row.errors.length > 0 ? (
                            <ul className="space-y-1 text-xs text-rose-600">
                              {row.errors.map((error, idx) => (
                                <li key={idx}>• {error}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                              Ready to upload
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${statusStyle}`}>
                            <span>
                              {row.status === 'pending' && 'Pending'}
                              {row.status === 'uploading' && 'Uploading'}
                              {row.status === 'success' && 'Uploaded'}
                              {row.status === 'already-registered' && 'Already registered'}
                              {row.status === 'error' && 'Failed'}
                              {row.status === 'blocked' && 'Blocked'}
                              {row.status === 'skipped' && 'Skipped'}
                            </span>
                          </div>
                          {row.message && (
                            <p className={`mt-1 text-xs ${messageClass}`}>{row.message}</p>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
